import { track } from "./analytics";
import { isSafeInternalPath } from "../utils/safeInternalPath";

// Talent email taps. Every button in a talent lifecycle email that points at
// the website carries ?em=<the email's name> (server: the talent email
// journeys): /open/payouts?em=bank, /requests?payouts=1&em=finish24,
// /qr/<slug>?em=qr … In the app the same links arrive as universal links, and
// components/DeepLinkHandler routes the SPA to the same path and query.
//
// components/EmailClickTracker hands every page change to noteEmailClick(): a
// valid name is logged as "email_clicked" (props.email = the name) and taken
// out of the address bar. Admin → Growth → Talent emails counts distinct
// (email, userId-or-anonymousId) next to each email's sends.
//
// Rules:
//   - Only a valid name counts: letters, digits, _ and -, 1 to 64 long (what
//     the server sends). Anything else is left in the URL untouched.
//   - Once per name per 30 minutes in this browser tab. A signed-out tap on
//     /open/payouts?em=bank goes on to /login?next=/requests?em=bank&payouts=1,
//     and a Google or Apple sign-in can reload the page, so the same name comes
//     back on /requests once signed in: logging it again would count one tap
//     twice. The names logged lately sit in sessionStorage (this tab only);
//     when storage can't be used, once per page load.
//   - Only `em` leaves the URL, by history.replaceState: same path, every
//     other param exactly as it was, same hash, the router's own history state
//     kept. Never a navigation: pages read ?payouts=1 / ?verify=1 / ?refer=1
//     on mount and must not be disturbed. The router keeps the em it already
//     read, so a later navigation built from the router's location can bring
//     em back to the address bar; the next page change takes it out again,
//     without a second log.
//   - Never throws: analytics, not product.

export const EMAIL_PARAM = "em";
const VALID_NAME = /^[A-Za-z0-9_-]{1,64}$/;
const SEEN_KEY = "rq-email-click";
const SEEN_FOR_MS = 30 * 60 * 1000;

/** The valid email name in a query string ("?payouts=1&em=bank" → "bank"), else null. */
export function emailFromSearch(search: string): string | null {
  try {
    const name = new URLSearchParams(search).get(EMAIL_PARAM);
    return name != null && VALID_NAME.test(name) ? name : null;
  } catch {
    return null;
  }
}

// A query part's name, decoded the way URLSearchParams decodes it ("%65m" is em too).
const paramName = (part: string): string => {
  const eq = part.indexOf("=");
  const raw = eq < 0 ? part : part.slice(0, eq);
  try {
    return decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    return raw;
  }
};

/** The query string without its em param(s): "?payouts=1&em=bank" → "?payouts=1". Every other param is kept byte for byte. */
export function searchWithoutEmail(search: string): string {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const kept = query.split("&").filter((part) => part !== "" && paramName(part) !== EMAIL_PARAM);
  return kept.length ? `?${kept.join("&")}` : "";
}

// ---- once per name per 30 minutes in this tab -------------------------------

type Seen = Record<string, number>;

// When sessionStorage can't be read or written: once per page load.
const seenThisPageLoad = new Set<string>();

const isRecent = (at: unknown, now: number): at is number => typeof at === "number" && Math.abs(now - at) < SEEN_FOR_MS;

// The stored map ({} when empty or not ours), or null when storage itself is unavailable.
function readSeen(): Seen | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Seen) : {};
  } catch {
    return {};
  }
}

// True the first time `name` is tapped in the window, and records the tap.
function isFirstTap(name: string, now: number): boolean {
  const seen = readSeen();
  if (seen) {
    if (isRecent(seen[name], now)) return false;
    // Older entries are dropped as the map is written, so it stays small.
    const next: Seen = {};
    for (const [other, at] of Object.entries(seen)) if (isRecent(at, now)) next[other] = at;
    next[name] = now;
    try {
      window.sessionStorage.setItem(SEEN_KEY, JSON.stringify(next));
      seenThisPageLoad.add(name);
      return true;
    } catch {
      /* full or blocked: the page-load rule below decides */
    }
  }
  if (seenThisPageLoad.has(name)) return false;
  seenThisPageLoad.add(name);
  return true;
}

// ---- the address bar ----------------------------------------------------------

function dropFromAddressBar(): void {
  try {
    const { pathname, search, hash } = window.location;
    if (!emailFromSearch(search)) return; // already gone, or not one of ours
    // Only ever this page's own path: a "//host" path would mean another site.
    if (!isSafeInternalPath(pathname)) return;
    window.history.replaceState(window.history.state, "", `${pathname}${searchWithoutEmail(search)}${hash}`);
  } catch {
    /* history refused: em just stays in the URL */
  }
}

// ---- public API ---------------------------------------------------------------

/**
 * One page change, with the router location's query string: a valid ?em= is
 * logged as "email_clicked" (once per name per 30 minutes in this tab), then
 * em leaves the address bar. Invalid or absent: nothing happens.
 */
export function noteEmailClick(search: string): void {
  const name = emailFromSearch(search);
  if (!name) return;
  try {
    if (isFirstTap(name, Date.now())) track("email_clicked", { props: { email: name } });
  } catch {
    /* analytics never breaks a page */
  }
  dropFromAddressBar();
}
